/**
 * Normalizes v0.8 A2UI protocol messages, components, and data model entries
 * into the v0.9-compatible internal format used by the renderer.
 *
 * v0.8 differences handled:
 * - Component format: { id, component: { "Text": { text: { literalString: "Hi" } } } }
 *   → { id, type: "Text", text: "Hi" }
 * - BoundValues: { literalString: "x" } → "x", { path: "/y" } → { path: "/y" }
 * - Children: { explicitList: [...] } → [...], { template: { dataBinding, componentId } } → { path, componentId }
 * - Data model: typed key-value entries → direct JSON
 * - Messages: surfaceUpdate, beginRendering → UPDATE_COMPONENTS, INIT_SURFACE
 */

import type { A2UIComponentDefinition } from '../types/A2UIComponents.types.tsx';
import type {
	V08BoundValue,
	V08ChildrenDefinition,
	V08ComponentWire,
	V08DataEntry,
} from '../types/A2UIProtocol.types.tsx';

// ── BoundValue normalization ────────────────────────────────────────────

/**
 * Convert a v0.8 BoundValue to a v0.9 DynamicValue.
 *
 * v0.8: { literalString: "x" } → "x"
 * v0.8: { path: "/a" } → { path: "/a" }
 * v0.8: { literalString: "default", path: "/a" } → { path: "/a" }
 * Already a primitive? Return as-is.
 */
export function normalizeBoundValue(value: unknown): unknown {
	if (value === null || value === undefined) {
		return value;
	}

	// Already a primitive — pass through (handles direct v0.9-style literals)
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return value;
	}

	// Array — pass through (v0.9 literal arrays)
	if (Array.isArray(value)) {
		return value;
	}

	if (typeof value !== 'object') {
		return value;
	}

	const obj = value as V08BoundValue;

	// If it has a `path`, it's a binding (path takes precedence over literal)
	if ('path' in obj && typeof obj.path === 'string') {
		return { path: obj.path };
	}

	// v0.8 typed literals
	if ('literalString' in obj && obj.literalString !== undefined) {
		return obj.literalString;
	}
	if ('literalNumber' in obj && obj.literalNumber !== undefined) {
		return obj.literalNumber;
	}
	if ('literalBoolean' in obj && obj.literalBoolean !== undefined) {
		return obj.literalBoolean;
	}
	if ('literalArray' in obj && obj.literalArray !== undefined) {
		return obj.literalArray;
	}

	// If it looks like a v0.9 FunctionCall ({ call, args }), pass through
	if ('call' in obj) {
		return value;
	}

	// If it looks like a v0.9 DataBinding ({ path }), pass through
	// (already handled above, but just in case)

	// Unknown shape — return as-is
	return value;
}

// ── Children normalization ──────────────────────────────────────────────

/**
 * Convert v0.8 children format to v0.9 ChildList.
 *
 * v0.8: { explicitList: ["a", "b"] } → ["a", "b"]
 * v0.8: { template: { dataBinding: { path: "/items" }, componentId: "tpl" } }
 *     → { path: "/items", componentId: "tpl" }
 * Already an array? Return as-is (v0.9 format).
 */
export function normalizeChildren(children: unknown): unknown {
	if (children === null || children === undefined) {
		return children;
	}

	// Already v0.9 format — array of IDs
	if (Array.isArray(children)) {
		return children;
	}

	if (typeof children !== 'object') {
		return children;
	}

	const obj = children as V08ChildrenDefinition;

	// v0.8 explicit list
	if ('explicitList' in obj && Array.isArray(obj.explicitList)) {
		return obj.explicitList;
	}

	// v0.8 template
	if ('template' in obj && obj.template) {
		const dataBinding = obj.template.dataBinding;
		const normalizedPath =
			typeof dataBinding === 'string'
				? dataBinding
				: typeof dataBinding?.path === 'string'
					? dataBinding.path
					: obj.template.componentId;
		return {
			componentId: obj.template.componentId,
			path: normalizedPath,
		};
	}

	// Already v0.9 ChildTemplate format ({ componentId, path })
	if ('componentId' in obj && 'path' in obj) {
		return children;
	}

	return children;
}

// ── Component normalization ─────────────────────────────────────────────

/**
 * Properties that are known to hold BoundValue/DynamicValue references.
 * These need recursive normalization from v0.8 to v0.9 format.
 */
const BOUND_VALUE_PROPS = new Set([
	'text',
	'url',
	'label',
	'value',
	'description',
	'min',
	'max',
	'title',
]);

const CHILDREN_PROPS = new Set(['children']);
const CHILD_REF_PROPS = new Set(['child', 'content', 'trigger']);

/**
 * v0.8 property renames → v0.9 property names.
 * Keys that exist in v0.8 wire format but were renamed in v0.9.
 */
const PROP_RENAMES: Record<string, string> = {
	usageHint: 'variant',
	tabItems: 'tabs',
};

/**
 * Recursively normalize component properties from v0.8 BoundValue format to v0.9 DynamicValue.
 */
function normalizeComponentProps(props: Record<string, unknown>): Record<string, unknown> {
	const result: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(props)) {
		const outputKey = PROP_RENAMES[key] ?? key;

		if (BOUND_VALUE_PROPS.has(key)) {
			result[outputKey] = normalizeBoundValue(value);
		} else if (CHILDREN_PROPS.has(key)) {
			result[outputKey] = normalizeChildren(value);
		} else if (CHILD_REF_PROPS.has(key)) {
			// child references are plain strings in both versions
			result[outputKey] = value;
		} else if (key === 'action') {
			result[outputKey] = normalizeAction(value);
		} else if (key === 'checks') {
			result[outputKey] = normalizeChecks(value);
		} else if (key === 'tabs' || key === 'tabItems') {
			result[outputKey] = normalizeTabs(value);
		} else if (key === 'options') {
			result[outputKey] = normalizeOptions(value);
		} else {
			result[outputKey] = value;
		}
	}

	return result;
}

/**
 * Normalize a v0.8 action.
 * v0.8 actions have: { name: "...", context: [{ key: "k", value: BoundValue }] }
 * v0.9 actions have: { event: "...", context: { k: DynamicValue } }
 * or: { functionCall: { call: "...", args: {...} } }
 */
function normalizeAction(action: unknown): unknown {
	if (!action || typeof action !== 'object') {
		return action;
	}

	const obj = action as Record<string, unknown>;

	// Already v0.9 format (has `event` or `functionCall`)
	if ('event' in obj || 'functionCall' in obj) {
		// Still normalize context values
		if ('context' in obj && typeof obj.context === 'object' && obj.context !== null) {
			if (Array.isArray(obj.context)) {
				// v0.8 context format: [{ key, value }] → { key: value }
				const normalizedContext: Record<string, unknown> = {};
				for (const entry of obj.context) {
					if (entry && typeof entry === 'object' && 'key' in entry && 'value' in entry) {
						normalizedContext[entry.key as string] = normalizeBoundValue(entry.value);
					}
				}
				return { ...obj, context: normalizedContext };
			}
			// v0.9 context format: { key: DynamicValue } — normalize values
			const ctx = obj.context as Record<string, unknown>;
			const normalizedContext: Record<string, unknown> = {};
			for (const [k, v] of Object.entries(ctx)) {
				normalizedContext[k] = normalizeBoundValue(v);
			}
			return { ...obj, context: normalizedContext };
		}
		return action;
	}

	// v0.8 format: { name: "...", context: [...] }
	if ('name' in obj) {
		const normalizedContext: Record<string, unknown> = {};
		if (Array.isArray(obj.context)) {
			for (const entry of obj.context) {
				if (entry && typeof entry === 'object' && 'key' in entry && 'value' in entry) {
					normalizedContext[entry.key as string] = normalizeBoundValue(entry.value);
				}
			}
		}
		return {
			event: obj.name,
			...(Object.keys(normalizedContext).length > 0 ? { context: normalizedContext } : {}),
		};
	}

	return action;
}

/**
 * Normalize v0.8 check rules.
 */
function normalizeChecks(checks: unknown): unknown {
	if (!Array.isArray(checks)) {
		return checks;
	}
	return checks.map((check) => {
		if (!check || typeof check !== 'object') {
			return check;
		}
		const c = check as Record<string, unknown>;
		return {
			...c,
			condition: c.condition ? normalizeBoundValue(c.condition) : c.condition,
		};
	});
}

/**
 * Normalize tab definitions.
 */
function normalizeTabs(tabs: unknown): unknown {
	if (!Array.isArray(tabs)) {
		return tabs;
	}
	return tabs.map((tab) => {
		if (!tab || typeof tab !== 'object') {
			return tab;
		}
		const t = tab as Record<string, unknown>;
		return {
			...t,
			title: t.title ? normalizeBoundValue(t.title) : t.title,
		};
	});
}

/**
 * Normalize choice picker options.
 */
function normalizeOptions(options: unknown): unknown {
	if (!Array.isArray(options)) {
		return options;
	}
	return options.map((opt) => {
		if (!opt || typeof opt !== 'object') {
			return opt;
		}
		const o = opt as Record<string, unknown>;
		return {
			...o,
			label: typeof o.label === 'object' ? normalizeBoundValue(o.label) : o.label,
			value: typeof o.value === 'object' ? normalizeBoundValue(o.value) : o.value,
		};
	});
}

/**
 * Convert a v0.8 wire-format component to the internal v0.9 component definition.
 *
 * v0.8: { id: "x", component: { "Text": { text: { literalString: "Hi" } } } }
 * v0.9: { id: "x", type: "Text", text: "Hi" }
 */
export function normalizeV08Component(wireComponent: V08ComponentWire): A2UIComponentDefinition {
	const { id, component, weight } = wireComponent;

	// The component wrapper has exactly one key — the component type
	const componentType = Object.keys(component)[0];
	const rawProps = component[componentType] ?? {};

	const normalizedProps = normalizeComponentProps(rawProps);

	return {
		id,
		type: componentType,
		...(weight != null ? { weight } : {}),
		...normalizedProps,
	} as A2UIComponentDefinition;
}

/**
 * Convert an array of v0.8 wire components to internal format.
 */
export function normalizeV08Components(
	wireComponents: V08ComponentWire[],
): A2UIComponentDefinition[] {
	return wireComponents.map(normalizeV08Component);
}

// ── Data model normalization ────────────────────────────────────────────

/**
 * Convert v0.8 typed data entries to a plain JSON object.
 *
 * v0.8: [{ key: "name", valueString: "Bob" }, { key: "age", valueNumber: 30 }]
 * v0.9: { name: "Bob", age: 30 }
 */
export function normalizeV08DataEntries(entries: V08DataEntry[]): Record<string, unknown> {
	const result: Record<string, unknown> = {};

	for (const entry of entries) {
		const { key } = entry;

		if (entry.valueString !== undefined) {
			result[key] = entry.valueString;
		} else if (entry.valueNumber !== undefined) {
			result[key] = entry.valueNumber;
		} else if (entry.valueBoolean !== undefined) {
			result[key] = entry.valueBoolean;
		} else if (entry.valueMap !== undefined) {
			result[key] = normalizeV08DataEntries(entry.valueMap);
		} else if (entry.valueList !== undefined) {
			result[key] = normalizeV08DataList(entry.valueList);
		} else {
			// No typed value — set to null
			result[key] = null;
		}
	}

	return result;
}

/**
 * Convert a v0.8 valueList (array of entries) to a JSON array.
 * Each entry in the list is itself a set of key-value entries forming an object.
 */
function normalizeV08DataList(entries: V08DataEntry[]): unknown[] {
	// Each entry at the top level of a list represents an item.
	// If items have keys, they're objects; otherwise treat as flat values.
	return entries.map((entry) => {
		if (entry.valueMap !== undefined) {
			return normalizeV08DataEntries(entry.valueMap);
		}
		if (entry.valueString !== undefined) {
			return entry.valueString;
		}
		if (entry.valueNumber !== undefined) {
			return entry.valueNumber;
		}
		if (entry.valueBoolean !== undefined) {
			return entry.valueBoolean;
		}
		if (entry.valueList !== undefined) {
			return normalizeV08DataList(entry.valueList);
		}
		return null;
	});
}

/**
 * Normalize v0.8 data model contents — could be an array of typed entries
 * or already a plain object (pass-through).
 */
export function normalizeV08DataContents(
	contents: V08DataEntry[] | Record<string, unknown>,
): Record<string, unknown> {
	if (Array.isArray(contents)) {
		return normalizeV08DataEntries(contents);
	}
	// Already a plain object
	return contents;
}

// ── Version detection ───────────────────────────────────────────────────

/**
 * Detect whether a message is v0.8 or v0.9 format.
 */
export function isV08Message(message: unknown): boolean {
	if (!message || typeof message !== 'object') {
		return false;
	}
	const msg = message as Record<string, unknown>;
	return 'surfaceUpdate' in msg || 'beginRendering' in msg || 'dataModelUpdate' in msg;
}

/**
 * Detect whether a component is in v0.8 wire format.
 * v0.8: component field is an object (wrapper), v0.9: component field is a string.
 */
export function isV08ComponentFormat(component: unknown): boolean {
	if (!component || typeof component !== 'object') {
		return false;
	}
	const obj = component as Record<string, unknown>;
	return (
		'component' in obj &&
		typeof obj.component === 'object' &&
		obj.component !== null &&
		!Array.isArray(obj.component)
	);
}
